import { useRef, useState, useCallback } from 'react';

export interface FocalPointPickerProps {
  src: string;
  focal: { x: number; y: number };
  onChange: (focal: { x: number; y: number }) => void;
}

export function FocalPointPicker({ src, focal, onChange }: FocalPointPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const updateFocal = useCallback((clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
    onChange({ x: Math.round(x), y: Math.round(y) });
  }, [onChange]);

  function handlePointerDown(e: React.PointerEvent) {
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updateFocal(e.clientX, e.clientY);
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!dragging) return;
    updateFocal(e.clientX, e.clientY);
  }

  function handlePointerUp() {
    setDragging(false);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-app-muted">Drag to set focal point</p>
        <p className="text-xs text-app-muted font-mono">{focal.x}% {focal.y}%</p>
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
        {/* Full image with crosshair */}
        <div
          ref={containerRef}
          className="relative rounded-xl overflow-hidden bg-app-border cursor-crosshair select-none touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <img src={src} alt="" className="w-full block" draggable={false} />

          {/* Crosshair overlay */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Vertical line */}
            <div
              className="absolute top-0 bottom-0 w-px bg-white/50"
              style={{ left: `${focal.x}%` }}
            />
            {/* Horizontal line */}
            <div
              className="absolute left-0 right-0 h-px bg-white/50"
              style={{ top: `${focal.y}%` }}
            />
            {/* Center dot */}
            <div
              className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3),0_2px_8px_rgba(0,0,0,0.4)]"
              style={{ left: `${focal.x}%`, top: `${focal.y}%` }}
            >
              <div className="absolute inset-[3px] rounded-full bg-gold" />
            </div>
          </div>

          {/* Dim areas outside the crop zone */}
          <div className="absolute inset-0 pointer-events-none bg-black/20" />
        </div>

        {/* Preview: how it looks cropped */}
        <div className="flex flex-col gap-1.5">
          <p className="text-caption2 text-app-muted uppercase tracking-wider">Preview</p>
          <div className="w-20 h-20 rounded-lg overflow-hidden bg-app-border border border-app-border">
            <img
              src={src}
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition: `${focal.x}% ${focal.y}%` }}
            />
          </div>
          <div className="w-20 h-12 rounded-lg overflow-hidden bg-app-border border border-app-border">
            <img
              src={src}
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition: `${focal.x}% ${focal.y}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
